import {cva} from 'platform/shared';

import {ReactNode} from 'react';

import {isNil} from 'ramda';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useFoundationTheme} from '../../hooks/useFoundationTheme';
import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {ValueByDevice} from '../../types/ValueByDevice';
import {cn} from '../../utils/cn';
import {ThemeColorTextPath} from '../../utils/foundationTheme';
import {getTextColorByPath} from '../../utils/getTextColorByPath';

export type DisplayAlign = 'left' | 'center' | 'right';

type DisplaySize = 1 | 2;

const DISPLAY_SIZE_CLASSES: Record<DisplaySize, string> = {
  1: 'text-[length:var(--display-size-1)] leading-[var(--display-leading-1)]',
  2: 'text-[length:var(--display-size-2)] leading-[var(--display-leading-2)]',
};

const DISPLAY_ALIGN_CLASSES: Record<DisplayAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const displayVariants = cva(
  '[font-family:var(--font-family-heading,Roboto,_sans-serif)] tracking-[var(--letter-spacing-display,0)]',
  {
    variants: {
      weight: {
        default: 'font-[var(--display-weight-default)]',
        alternative: 'font-[var(--display-weight-alternative)]',
      },
    },
    defaultVariants: {
      weight: 'default',
    },
  }
);

export interface DisplayProps extends TestIdProps {
  size: DisplaySize | ValueByDevice<DisplaySize>;
  alternative?: true;
  color?: ThemeColorTextPath;
  children?: ReactNode | Nullish;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  align?: DisplayAlign | ValueByDevice<DisplayAlign>;
}

export function Display(props: DisplayProps) {
  const theme = useFoundationTheme();
  const align = useResponsivePropValue(props.align);
  const size = useResponsivePropValue(props.size);

  return (
    <div
      role="heading"
      aria-level={props.headingLevel ?? size}
      data-testid={suffixTestId('display', props)}
      className={cn(
        displayVariants({weight: props.alternative ? 'alternative' : 'default'}),
        DISPLAY_SIZE_CLASSES[size],
        isNil(align) ? undefined : DISPLAY_ALIGN_CLASSES[align]
      )}
      style={{color: getTextColorByPath(props.color ?? 'primary', theme)}}
    >
      {props.children}
    </div>
  );
}
